import { datePresetCond } from './filters.js';
const NUM = '(-?\\d+(?:\\.\\d+)?)';
const CMP = [
    [/^(이상|부터|넘거나 같은)/, '>='], [/^(초과|넘는|넘은|보다 큰|보다 높은|넘게)/, '>'],
    [/^(이하|까지)/, '<='], [/^(미만|보다 작은|보다 낮은|안 되는|안되는)/, '<'], [/^(인|짜리|=)/, '='],
];
const DESC = /(내림차순|높은\s*순|많은\s*순|큰\s*순|최신\s*순|늦은\s*순|역순)/;
const ASC = /(오름차순|낮은\s*순|적은\s*순|작은\s*순|오래된\s*순|빠른\s*순|순으로|순서대로|정렬)/;
/** 부정 낱말 (앞 조건을 뒤집음) */
const NEG = /(제외하고|제외한|제외|빼고|뺀|말고|아니고|아닌|아니면|아님|아니)(?=$|[\s,.·/])/g;
/** OR 낱말 (앞뒤 조건을 OR 로) — 값 목록은 ',' '/' 로도 OR(목록) */
const OR = /(또는|혹은|이거나|거나|이나|\bor\b)(?=$|[\s,.·/])/g;
/** 의료 도메인 기본 별칭 (컬럼 id → 부르는 말). 병원별로 확장 — 사전이라 결과가 결정적 */
export const MEDICAL_ALIASES = {
    sbp: ['수축기혈압', '수축기', 'sbp', '혈압'], dbp: ['이완기혈압', '이완기', 'dbp'], hr: ['맥박', '심박수', 'hr', 'pr'],
    bt: ['체온', 'bt'], glu: ['혈당', 'bst', 'glucose'], age: ['나이', '연령'], amt: ['금액', '진료비', '비용'],
    qty: ['수량'], dose: ['용량', '1회량'], dept: ['진료과', '과'], ward: ['병동'], doctor: ['주치의', '의사', '담당의'], status: ['상태'],
    ins: ['보험', '보험유형', '급여'], ptNm: ['환자명', '이름', '성명'], name: ['환자명', '이름', '성명'], ptno: ['등록번호', '환자번호'],
    ordDt: ['처방일', '처방'], rsltDt: ['검사일', '날짜'], rslt: ['결과', '결과값', '수치'], labNm: ['검사명', '검사'], flag: ['판정'],
};
const SEX = { 남자: 'M', 남성: 'M', 여자: 'F', 여성: 'F' };
const OPK = { '>=': '이상', '>': '초과', '<=': '이하', '<': '미만', '=': '=', '!=': '≠' };
const FLIP = { '>=': '<', '<': '>=', '>': '<=', '<=': '>', '=': '!=', '!=': '=', in: 'notIn', notIn: 'in', between: 'notBetween', notBetween: 'between' };
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fill = (k, v) => (v ? k.replace(/\{(\w+)\}/g, (m, x) => (x in v ? String(v[x]) : m)) : k);
/** 상대 날짜 표현 → 기간 (순수). 못 알아들으면 null */
export function relativeDateRange(expr, today = new Date()) {
    const r = relDate(expr, today);
    return r && { from: r.from, to: r.to };
}
/** 기간 + 설명 키(번역용, 예: '최근 {n}일') */
function relDate(expr, today = new Date()) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const e = expr.replace(/\s+/g, '');
    const pre = (p, key) => { const c = datePresetCond(p, d); return { from: c.value, to: c.value2 ?? c.value, key }; };
    if (e === '오늘' || e === '금일')
        return pre('today', '오늘');
    if (e === '어제' || e === '전일')
        return pre('yesterday', '어제');
    if (e === '그제' || e === '그저께') {
        const x = new Date(d);
        x.setDate(x.getDate() - 2);
        return { from: ymd(x), to: ymd(x), key: '그제' };
    }
    if (/^(이번주|금주)$/.test(e))
        return pre('thisWeek', '이번 주');
    if (/^(지난주|저번주)$/.test(e))
        return pre('lastWeek', '지난주');
    if (/^(이번달|이달|금월)$/.test(e))
        return pre('thisMonth', '이번 달');
    if (/^(지난달|저번달|전월)$/.test(e))
        return pre('lastMonth', '지난달');
    if (/^(올해|금년|이번해)$/.test(e))
        return pre('thisYear', '올해');
    if (/^(작년|지난해|전년)$/.test(e))
        return { from: `${d.getFullYear() - 1}-01-01`, to: `${d.getFullYear() - 1}-12-31`, key: '작년' };
    const m = /^(?:최근|지난)(\d+)(일|주|주일|개월|달|년)(?:간|동안)?$/.exec(e);
    if (m) {
        const n = +m[1], u = m[2], x = new Date(d);
        if (n <= 0 || n > 3650)
            return null;
        let key = '최근 {n}일';
        if (u === '일')
            x.setDate(x.getDate() - (n - 1));
        else if (u === '주' || u === '주일') {
            x.setDate(x.getDate() - 7 * n + 1);
            key = '최근 {n}주';
        }
        else if (u === '년') {
            x.setFullYear(x.getFullYear() - n);
            x.setDate(x.getDate() + 1);
            key = '최근 {n}년';
        }
        else {
            x.setMonth(x.getMonth() - n);
            x.setDate(x.getDate() + 1);
            key = '최근 {n}개월';
        }
        return { from: ymd(x), to: ymd(d), key, vars: { n } };
    }
    return null;
}
const DATE_EXPR = /(최근|지난)\s*\d+\s*(?:일|주일|주|개월|달|년)(?:간|동안)?|오늘|금일|어제|전일|그저께|그제|이번\s?주|금주|지난\s?주|저번\s?주|이번\s?달|이달|금월|지난\s?달|저번\s?달|전월|올해|금년|작년|지난해|전년/g;
export function parseNlQuery(text, columns, aliases = MEDICAL_ALIASES, opts = {}) {
    const t = opts.t ?? fill;
    const sort = [], unknown = [], items = [];
    let ord = 0;
    let s = (' ' + text.replace(/\s+/g, ' ').trim() + ' ').toLowerCase();
    const blank = (start, len) => { s = s.slice(0, start) + ' '.repeat(len) + s.slice(start + len); };
    const add = (f, start, end, extra = {}) => { items.push({ kind: 'f', f, start, end, ord: ord++, ...extra }); };
    const has = (id) => columns.some(c => c.id === id);
    // 별칭 표 (긴 것부터)
    const colWords = [];
    for (const c of columns)
        for (const w of new Set([c.header, ...(aliases[c.id] ?? [])].filter(Boolean)))
            colWords.push([w.toLowerCase(), c]);
    colWords.sort((a, b) => b[0].length - a[0].length);
    const header = (id) => columns.find(c => c.id === id)?.header ?? id;
    // 0) 부정·OR 낱말 표시 (위치만 기억하고 지움)
    for (const [re, kind] of [[NEG, 'not'], [OR, 'or']]) {
        s = s.replace(re, (m, _w, off) => { items.push({ kind, start: off, end: off + m.length }); return ' '.repeat(m.length); });
    }
    // 1) "N세 이상/미만" — 나이 축약 (age 컬럼이 있을 때만)
    const ageWords = colWords.filter(([, c]) => c.id === 'age').map(([w]) => escapeRe(w));
    if (has('age'))
        s = s.replace(new RegExp(`(?:(?:${ageWords.join('|')})\\s*(?:이|가|은|는)?\\s*)?${NUM}\\s*세\\s*(이상|초과|이하|미만|부터|까지)?`, 'g'), (m, n, op, off) => {
            const o = CMP.find(([re]) => re.test(op || '이상'))?.[1] ?? '>=';
            add({ col: 'age', op: o, value: +n }, off, off + m.length);
            return ' '.repeat(m.length);
        });
    // 2) "<컬럼> N <비교어>"
    for (const [w, c] of colWords) {
        if (c.type !== 'number' && c.type !== 'date')
            continue;
        const re = new RegExp(`${escapeRe(w)}\\s*(?:이|가|은|는)?\\s*${NUM}\\s*([가-힣=]+(?:\\s(?:큰|작은|높은|낮은|되는))?)?`, 'gi');
        s = s.replace(re, (m, n, tail = '', off) => {
            const op = CMP.find(([r]) => r.test(String(tail).trim()))?.[1];
            if (!op)
                return m;
            add({ col: c.id, op, value: +n }, off, off + m.length);
            return ' '.repeat(m.length);
        });
    }
    // 2-1) 상대 날짜: "지난주", "처방일 최근 7일", "이번달 검사" — 앞뒤 날짜 컬럼 말이 없으면 첫 날짜 컬럼
    const dateCols = colWords.filter(([, c]) => c.type === 'date');
    if (dateCols.length) {
        const found = [];
        s.replace(DATE_EXPR, (m, ...a) => { found.push({ m, off: a[a.length - 2] }); return m; });
        for (const { m, off } of found) {
            const r = relDate(m, opts.today);
            if (!r)
                continue;
            let col = dateCols[0][1], start = off, end = off + m.length;
            const before = s.slice(0, off), after = s.slice(end);
            for (const [w, c] of dateCols) {
                const b = new RegExp(`(?:^|\\s)(${escapeRe(w)})\\s*(?:이|가|은|는|의)?\\s*$`).exec(before);
                if (b) {
                    col = c;
                    start = b.index + b[0].indexOf(b[1]);
                    break;
                }
                const a = new RegExp(`^\\s*(?:의|에|에\\s*한|중)?\\s*(${escapeRe(w)})(?=$|[\\s,])`).exec(after);
                if (a) {
                    col = c;
                    end = end + a[0].length;
                    break;
                }
            }
            blank(start, end - start);
            add({ col: col.id, op: 'between', value: r.from, value2: r.to }, start, end, { label: t(r.key, r.vars) });
        }
    }
    // 3) 정렬: "<컬럼> 높은 순" / "<컬럼> 내림차순" / "<컬럼>순"
    for (const [w, c] of colWords) {
        const re = new RegExp(`${escapeRe(w)}\\s*(?:이|가|을|를)?\\s*(${DESC.source}|${ASC.source}|순)`, 'i');
        const m = re.exec(s);
        if (m && !sort.some(k => k.col === c.id)) {
            sort.push({ col: c.id, dir: DESC.test(m[1]) ? 'desc' : 'asc' });
            blank(m.index, m[0].length);
        }
    }
    // 4) 성별 (sex 컬럼이 있을 때)
    if (has('sex'))
        s = s.replace(/(남자|남성|여자|여성)/g, (w, _x, off) => { add({ col: 'sex', op: 'in', value: [SEX[w]] }, off, off + w.length, { code: true, words: [w] }); return ' '.repeat(w.length); });
    // 5) 코드값(데이터 사전) 최장일치: "내과", "건강보험" … (같은 컬럼 여러 값 = 목록 OR)
    const cand = [];
    for (const c of columns)
        if (c.type === 'string' && c.values && c.values.length <= 5000)
            for (const v of c.values)
                if (v && v.length >= 2)
                    cand.push([v, c]);
    cand.sort((a, b) => b[0].length - a[0].length);
    for (const [v, c] of cand) {
        const re = new RegExp(`(^|[\\s,·/및와과])(${escapeRe(v.toLowerCase())})(?=$|[\\s,·/및와과]|환자|에서|의|만|중|인|이|가|은|는|을|를|도)`, 'g');
        s = s.replace(re, (_m, p1, w, off) => { add({ col: c.id, op: 'in', value: [v] }, off + p1.length, off + p1.length + w.length, { code: true, words: [v] }); return p1 + ' '.repeat(w.length); });
    }
    // 6) 위치로 부정·OR 붙이기 — 사이에 조사·구분자만 있으면 "붙어 있음"
    items.sort((a, b) => a.start - b.start || a.end - b.end);
    const GAP = /^[\s,·/및와과이가은는을를도의인]*$/;
    const adj = (a, b) => !!b && b.start >= a.end && GAP.test(s.slice(a.end, b.start));
    for (let i = items.length - 1; i >= 0; i--) {
        const it = items[i], nx = items[i + 1];
        if (it.kind !== 'f' || !nx || !adj(it, nx))
            continue;
        if (nx.kind === 'not') {
            it.neg = true;
            continue;
        }
        // "내과, 외과 제외" / "내과 또는 외과 제외" → 목록 전체 부정 (같은 컬럼 코드값이 이어지면 뒤 값의 부정을 따름)
        if (!it.code)
            continue;
        let j = i + 1;
        while (items[j]?.kind === 'or' && adj(items[j], items[j + 1]))
            j++;
        const nf = items[j];
        if (nf?.kind === 'f' && nf.code && nf.f.col === it.f.col)
            it.neg = nf.neg;
    }
    const fItems = items.filter((x) => x.kind === 'f');
    // 코드값: (컬럼, 부정) 별로 하나의 목록 조건
    const merged = new Map(), owner = new Map();
    const finals = [];
    for (const it of [...fItems].sort((a, b) => a.ord - b.ord)) {
        if (it.code) {
            const k = it.f.col + (it.neg ? '\u0001!' : '');
            const m = merged.get(k);
            if (m) {
                for (const v of it.f.value)
                    if (!m.f.value.includes(v)) {
                        m.f.value.push(v);
                        m.words.push(it.words[it.f.value.indexOf(v)] ?? v);
                    }
                owner.set(it, m);
                continue;
            }
            const c = { ...it, f: { ...it.f, value: [...it.f.value] }, words: [...(it.words ?? [])] };
            merged.set(k, c);
            owner.set(it, c);
            finals.push(c);
        }
        else {
            owner.set(it, it);
            finals.push(it);
        }
    }
    for (const it of finals)
        if (it.neg)
            it.f.op = FLIP[it.f.op] ?? it.f.op;
    // OR: 앞 조건과 뒤 조건을 한 묶음으로 (같은 목록이면 이미 OR)
    const parent = new Map(finals.map(f => [f, f]));
    const root = (f) => { let r = f; while (parent.get(r) !== r)
        r = parent.get(r); return r; };
    items.forEach((it, i) => {
        if (it.kind !== 'or')
            return;
        let a = i - 1;
        while (items[a]?.kind === 'not')
            a--;
        const b = i + 1;
        const pa = items[a], pb = items[b];
        if (pa?.kind !== 'f' || pb?.kind !== 'f' || !adj(pa, items[a + 1]) || !adj(it, pb))
            return;
        const ra = root(owner.get(pa)), rb = root(owner.get(pb));
        if (ra !== rb) {
            if (ra.ord <= rb.ord)
                parent.set(rb, ra);
            else
                parent.set(ra, rb);
        }
    });
    // 설명
    const descr = (it) => {
        const f = it.f, H = header(f.col);
        if (f.col === 'sex' && it.words)
            return `${H} ${f.op === 'notIn' ? '≠' : '='} ${it.words.join(', ')}`;
        if (f.op === 'in' || f.op === 'notIn')
            return `${H} ${f.op === 'in' ? '∈' : '∉'} {${(it.words ?? f.value).join(', ')}}`;
        if (f.op === 'between' || f.op === 'notBetween')
            return `${H} ${f.op === 'notBetween' ? t('범위 밖') + ' ' : ''}${f.value} ~ ${f.value2}${it.label ? ` (${it.label})` : ''}`;
        return `${H} ${t(OPK[f.op] ?? f.op)} ${f.value}`;
    };
    const filters = [], explain = [];
    for (const it of finals) {
        if (root(it) !== it)
            continue;
        const group = finals.filter(x => root(x) === it);
        if (group.length === 1) {
            filters.push(it.f);
            explain.push(descr(it));
        }
        else {
            filters.push({ ...it.f, or: group.slice(1).map(x => x.f) });
            explain.push(`(${group.map(descr).join(` ${t('또는')} `)})`);
        }
    }
    for (const k of sort)
        explain.push(t(k.dir === 'desc' ? '{col} 내림차순 정렬' : '{col} 오름차순 정렬', { col: header(k.col) }));
    // 남은 말 → 해석 못 함
    const STOP = /^(환자|환자만|보여줘|보여|줘|조회|검색|찾아|찾아줘|중|중에|중에서|인|의|만|그리고|및|와|과|에서|사람|목록|리스트|해줘|정렬해줘|이|가|을|를|은|는|도|으로|로|건|것|한|된)$/;
    for (const w of s.split(/[\s,·/]+/))
        if (w && !STOP.test(w))
            unknown.push(w);
    return { filters, sort, explanation: explain.join(', '), unknown, confident: unknown.length === 0 && (filters.length + sort.length) > 0 };
}
